/**
 * LeadRegisterPanel
 *
 * Admin → "Leads" tab. Shows the pipeline of enquiries (logged from the
 * homepage Lead Register quick-form). Filters by status + date + free
 * search. Inline status change, edit, delete, Excel export. Admins and
 * managers both have access.
 */
import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Download, Search, Trash2, Pencil, Phone, RefreshCw,
  CheckCircle2, RotateCcw, Camera, X,
} from 'lucide-react';
import { api, API } from '../../lib/store';

// Legacy status colour map kept ONLY to render badges on old records.
// New records always use the boolean `done` field; the STATUSES list
// (New/Pending/Closed/Lost) is no longer surfaced in this panel — the
// UI collapsed to Active / Done to match the LeadFormDialog change.
const LEGACY_STATUS_LABELS = {
  new: 'New', pending: 'Pending', closed: 'Closed', lost: 'Lost',
  hot: 'Hot', warm: 'Warm', cold: 'Cold', converted: 'Converted',
};
const DONE_LEGACY_STATUSES = new Set(['closed', 'lost', 'converted']);
const isLeadDone = (lead) =>
  lead?.done === true || DONE_LEGACY_STATUSES.has((lead?.status || '').toLowerCase());
const MAX_SITE_IMAGES = 5;
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

const SOURCES = ['Phone', 'Referral', 'Instagram', 'Facebook', 'Exhibition', 'Walk-by', 'Website', 'Other'];
const INTERESTS = ['Plywood', 'Laminates', 'Veneers', 'Highlighters', 'Hardware', 'Doors', 'ACP', 'Adhesives', 'Wooden Flooring', 'WPC/PVC', 'Multiple'];
const TIMELINES = ['This week', 'This month', '1-3 months', 'Later'];

export default function LeadRegisterPanel() {
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({ all: 0, active: 0, done: 0 });
  const [search, setSearch] = useState('');
  // Chips are now All / Closed / Pending / Lost (order_status).
  // The list is fetched without a status filter, then bucketed client-side
  // so missing/null/'' order_status folds into Pending — same rule as the
  // backend and the My Registrations tab.
  const [statusFilter, setStatusFilter] = useState('pending');
  // Visit-date range filter — used to answer "which leads actually visited in this window".
  // Logged (created_at) date filter removed per business request — the Visit range is the
  // only date lens admins actually use for leads triage.
  const [visitFrom, setVisitFrom] = useState('');
  const [visitTo, setVisitTo] = useState('');
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const params = { search: search || undefined };
      // No longer sending status to the server — the client
      // buckets by order_status locally so legacy done/status leads
      // continue to render without a backend migration.
      if (visitFrom) params.visit_date_from = visitFrom;
      if (visitTo) params.visit_date_to = visitTo;
      const res = await api.listLeads(params);
      setLeads(res.data.leads || []);
      setTotal(res.data.total || 0);
      setCounts(res.data.counts || {});
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load leads');
    } finally {
      setLoading(false);
    }
  };

  // Re-fetch when filters change. We schedule via queueMicrotask so the
  // state-setter calls inside fetchLeads run *after* the effect returns —
  // required to satisfy `react-hooks/set-state-in-effect` on React 19.
  useEffect(() => {
    let cancelled = false;
    queueMicrotask(() => { if (!cancelled) fetchLeads(); });
    return () => { cancelled = true; };
    // `search`/`fetchLeads` deliberately excluded — search fetches only on
    // Enter key or the Apply button, never per keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visitFrom, visitTo]);

  const handleDelete = async (lead) => {
    if (!window.confirm(`Delete lead for ${lead.customer_name}? This cannot be undone.`)) return;
    try {
      await api.deleteLead(lead.id);
      toast.success('Lead deleted');
      fetchLeads();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleToggleDone = async (lead) => {
    const nextDone = !isLeadDone(lead);
    try {
      const token = localStorage.getItem('heda_token') || '';
      await axios.patch(
        `${API}/leads/${lead.id}/done`,
        { done: nextDone },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
      toast.success(nextDone ? 'Marked Done' : 'Reopened');
      fetchLeads();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    }
  };

  const handleExport = async () => {
    try {
      const params = { search: search || undefined };
      params.status = statusFilter;
      if (visitFrom) params.visit_date_from = visitFrom;
      if (visitTo) params.visit_date_to = visitTo;
      const res = await api.exportLeadsXlsx(params);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `leads_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Failed to export Excel');
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-leads-panel">
      {/* Header + summary chips */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-base sm:text-lg font-bold text-[#1A1C16] flex items-center gap-2">
            <Phone className="w-4 h-4 text-[#2F6B8A]" /> Lead Register
            <Badge variant="outline" className="ml-1 text-[10px] font-mono-data">{counts.all}</Badge>
          </h2>
          <p className="text-[11px] text-[#8F9485] mt-0.5">
            External enquiries before they walk in. WhatsApp reminders fire on follow-up date.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={fetchLeads} disabled={loading} data-testid="leads-refresh-btn">
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} />Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={handleExport} data-testid="leads-export-btn">
            <Download className="w-3.5 h-3.5 mr-1.5" />Excel
          </Button>
        </div>
      </div>

      {/* All / Closed / Pending / Lost dot-chip filter with
          counts. Consistent with the same widget on MyRegistrationsPage's
          Walk-ins & Leads tabs and the Staff-tab quotation chips.
          Missing/null/'' order_status buckets under Pending. */}
      <div className="flex flex-wrap gap-1.5">
        {(() => {
          const buckets = { all: leads.length, closed: 0, pending: 0, lost: 0 };
          leads.forEach((x) => {
            const s = (x?.order_status || '').toLowerCase();
            buckets[(s === 'closed' || s === 'lost') ? s : 'pending'] += 1;
          });
          return [
            { v: 'all',     label: 'All',     dot: 'bg-[#8F9485]', active: 'bg-[#8F9485]/10 border-[#8F9485]/40 text-[#5C6150]' },
            { v: 'closed',  label: 'Closed',  dot: 'bg-[#2E7D32]', active: 'bg-[#2E7D32]/10 border-[#2E7D32]/40 text-[#1B5E20]' },
            { v: 'pending', label: 'Pending', dot: 'bg-[#F9A825]', active: 'bg-[#F9A825]/10 border-[#F9A825]/40 text-[#8B6914]' },
            { v: 'lost',    label: 'Lost',    dot: 'bg-[#C62828]', active: 'bg-[#C62828]/10 border-[#C62828]/40 text-[#8B2E2E]' },
          ].map((chip) => (
            <button
              key={chip.v}
              onClick={() => setStatusFilter(chip.v)}
              className={
                'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-medium transition-colors ' +
                (statusFilter === chip.v ? chip.active : 'bg-white text-[#5C6150] border-[#E2E6D9] hover:bg-[#F2F4EF]')
              }
              data-testid={`leads-filter-${chip.v}`}
            >
              <span className={`w-2 h-2 rounded-full ${chip.dot}`} />
              {chip.label} · {buckets[chip.v]}
            </button>
          ));
        })()}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-end">
        <div className="flex-1 min-w-[180px]">
          <Label className="text-[10px] text-[#5C6150]">Search</Label>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#8F9485]" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') fetchLeads(); }}
              placeholder="Name, phone, interest, notes…"
              className="pl-8 h-8 text-xs"
              data-testid="leads-search-input"
            />
          </div>
        </div>
        <div>
          <Label className="text-[10px] text-[#5C6150]">Visit From</Label>
          <Input type="date" value={visitFrom} onChange={(e) => setVisitFrom(e.target.value)} className="h-8 text-xs" data-testid="leads-visit-from" />
        </div>
        <div>
          <Label className="text-[10px] text-[#5C6150]">Visit To</Label>
          <Input type="date" value={visitTo} onChange={(e) => setVisitTo(e.target.value)} className="h-8 text-xs" data-testid="leads-visit-to" />
        </div>
        <Button size="sm" onClick={fetchLeads} variant="outline">Apply</Button>
        {(visitFrom || visitTo) && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => { setVisitFrom(''); setVisitTo(''); }}
            className="text-[10px] h-7"
            data-testid="leads-clear-visit-filter"
          >
            Clear Visit
          </Button>
        )}
      </div>

      {/* Table */}
      {leads.length === 0 ? (
        <Card className="border-[#E2E6D9]">
          <CardContent className="py-10 text-center text-sm text-[#8F9485] italic">
            {loading ? 'Loading…' : 'No leads yet. They show up here when staff log a new enquiry from the homepage.'}
          </CardContent>
        </Card>
      ) : (
        <div className="border border-[#E2E6D9] rounded-md overflow-x-auto bg-white">
          <table className="w-full text-xs min-w-[920px]">
            <thead className="bg-[#F2F4EF] text-[#5C6150]">
              <tr>
                <th className="text-left py-2 px-3">Customer</th>
                <th className="text-left py-2 px-3">Source / Interest</th>
                <th className="text-left py-2 px-3">Budget / Timeline</th>
                <th className="text-left py-2 px-3">Follow-up</th>
                <th className="text-left py-2 px-3">Photos</th>
                <th className="text-left py-2 px-3">State</th>
                <th className="text-left py-2 px-3">Salesperson</th>
                <th className="text-right py-2 px-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {leads
                .filter((l) => {
                  if (statusFilter === 'all') return true;
                  const s = (l?.order_status || '').toLowerCase();
                  const bucket = (s === 'closed' || s === 'lost') ? s : 'pending';
                  return bucket === statusFilter;
                })
                .map((l) => {
                const done = isLeadDone(l);
                const legacyLabel = LEGACY_STATUS_LABELS[(l.status || '').toLowerCase()];
                const photos = Array.isArray(l.site_images) ? l.site_images : [];
                return (
                  <tr key={l.id} className="border-t border-[#E2E6D9] hover:bg-[#FAFBF7]" data-testid={`lead-row-${l.id}`}>
                    <td className="py-2 px-3">
                      <p className="font-semibold text-[#1A1C16]">{l.customer_name}</p>
                      <p className="text-[10px] text-[#8F9485]">{l.customer_phone || '—'}</p>
                      {l.location && (
                        <p className="text-[10px] text-[#5C6150]" data-testid={`lead-location-${l.id}`}>📍 {l.location}</p>
                      )}
                      {(l.architect_name || l.carpenter_name) && (
                        <p className="text-[10px] text-[#5C6150]" data-testid={`lead-refers-${l.id}`}>
                          {l.architect_name && <span>👷 Arch: {l.architect_name}</span>}
                          {l.architect_name && l.carpenter_name && <span> · </span>}
                          {l.carpenter_name && <span>🔨 Carp: {l.carpenter_name}</span>}
                        </p>
                      )}
                      {l.visit_date && (
                        <p className="text-[10px] text-[#8F9485] font-mono-data" data-testid={`lead-visit-date-${l.id}`}>Visit: {l.visit_date}</p>
                      )}
                    </td>
                    <td className="py-2 px-3">
                      <p className="text-[#1A1C16]">{l.source || '—'}</p>
                      <p className="text-[10px] text-[#8F9485]">{l.interest_area || '—'}</p>
                    </td>
                    <td className="py-2 px-3">
                      <p className="text-[#1A1C16]">{l.budget_range || '—'}</p>
                      <p className="text-[10px] text-[#8F9485]">{l.timeline || '—'}</p>
                    </td>
                    <td className="py-2 px-3 font-mono-data text-[#1A1C16]">{l.follow_up_date || '—'}</td>
                    <td className="py-2 px-3" data-testid={`lead-photos-${l.id}`}>
                      {photos.length === 0 ? (
                        <span className="text-[10px] text-[#8F9485]">—</span>
                      ) : (
                        <div className="flex flex-wrap gap-1 max-w-[110px]">
                          {photos.slice(0, 4).map((name) => (
                            <a
                              key={name}
                              href={`${API}/uploads/${name}`}
                              target="_blank"
                              rel="noreferrer"
                              className="block w-8 h-8 rounded overflow-hidden border border-[#E2E6D9] hover:ring-2 hover:ring-[#2F6B8A]"
                              data-testid={`lead-photo-thumb-${l.id}-${name}`}
                              title="Open in new tab"
                            >
                              <img src={`${API}/uploads/${name}`} alt="" className="w-full h-full object-cover" loading="lazy" />
                            </a>
                          ))}
                          {photos.length > 4 && (
                            <span className="text-[9px] text-[#8F9485] self-center">+{photos.length - 4}</span>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="py-2 px-3">
                      <div className="flex flex-col items-start gap-1">
                        <Badge
                          variant="outline"
                          className={`text-[10px] py-0 px-1.5 ${done ? 'bg-green-100 text-green-800 border-green-300' : 'bg-blue-100 text-blue-800 border-blue-300'}`}
                          data-testid={`lead-state-badge-${l.id}`}
                        >
                          {done ? 'DONE' : 'ACTIVE'}
                        </Badge>
                        {!done && legacyLabel && legacyLabel !== 'New' && (
                          <span className="text-[9px] text-[#8F9485]">was: {legacyLabel}</span>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleToggleDone(l)}
                          className="h-6 text-[10px] px-2 mt-0.5"
                          data-testid={`lead-toggle-done-${l.id}`}
                        >
                          {done ? <><RotateCcw className="w-3 h-3 mr-1" />Reopen</> : <><CheckCircle2 className="w-3 h-3 mr-1" />Mark Done</>}
                        </Button>
                      </div>
                    </td>
                    <td className="py-2 px-3 text-[10px] text-[#5C6150]">{l.salesperson_name || '—'}</td>
                    <td className="py-2 px-3 text-right">
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setEditing(l)} data-testid={`lead-edit-${l.id}`}>
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-600 hover:bg-red-50" onClick={() => handleDelete(l)} data-testid={`lead-delete-${l.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <LeadEditDialog
          lead={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); fetchLeads(); }}
        />
      )}
    </div>
  );
}

function LeadEditDialog({ lead, onClose, onSaved }) {
  const [form, setForm] = useState(lead);
  const [saving, setSaving] = useState(false);
  // Existing photos live on the lead doc (`site_images`). We keep a
  // local mirror so removes reflect immediately without re-fetching.
  const [existingPhotos, setExistingPhotos] = useState(
    Array.isArray(lead.site_images) ? lead.site_images : [],
  );
  const [pendingPhotos, setPendingPhotos] = useState([]);
  const [pendingPreviews, setPendingPreviews] = useState([]);
  const [uploading, setUploading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setSel = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => () => {
    pendingPreviews.forEach((u) => URL.revokeObjectURL(u));
  }, [pendingPreviews]);

  const remainingSlots = MAX_SITE_IMAGES - existingPhotos.length - pendingPhotos.length;

  const handlePickPhotos = (e) => {
    const chosen = Array.from(e.target.files || []);
    if (chosen.length === 0) return;
    if (remainingSlots <= 0) {
      toast.error(`Max ${MAX_SITE_IMAGES} site photos per lead`);
      e.target.value = '';
      return;
    }
    const accepted = [];
    const previews = [];
    for (const file of chosen.slice(0, remainingSlots)) {
      if (!file.type.startsWith('image/')) { toast.error(`${file.name}: not an image`); continue; }
      if (file.size > MAX_IMAGE_BYTES) { toast.error(`${file.name}: exceeds 5 MB`); continue; }
      accepted.push(file);
      previews.push(URL.createObjectURL(file));
    }
    setPendingPhotos((p) => [...p, ...accepted]);
    setPendingPreviews((p) => [...p, ...previews]);
    e.target.value = '';
  };

  const removePendingPhoto = (idx) => {
    setPendingPhotos((prev) => prev.filter((_, i) => i !== idx));
    setPendingPreviews((prev) => {
      const removed = prev[idx];
      if (removed) URL.revokeObjectURL(removed);
      return prev.filter((_, i) => i !== idx);
    });
  };

  const removeExistingPhoto = async (filename) => {
    if (!window.confirm('Remove this site photo?')) return;
    try {
      const token = localStorage.getItem('heda_token') || '';
      await axios.delete(`${API}/leads/${lead.id}/images/${filename}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setExistingPhotos((prev) => prev.filter((n) => n !== filename));
      toast.success('Photo removed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove photo');
    }
  };

  const handleSave = async () => {
    if (!form.customer_name?.trim()) {
      toast.error('Customer name is required');
      return;
    }
    setSaving(true);
    try {
      await api.updateLead(lead.id, form);
      if (pendingPhotos.length > 0) {
        setUploading(true);
        try {
          const fd = new FormData();
          pendingPhotos.forEach((file) => fd.append('files', file, file.name));
          const token = localStorage.getItem('heda_token') || '';
          const res = await axios.post(`${API}/leads/${lead.id}/images`, fd, {
            headers: {
              'Content-Type': 'multipart/form-data',
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
          });
          setExistingPhotos(res.data?.site_images || existingPhotos);
        } catch (upErr) {
          toast.error(upErr.response?.data?.detail || 'Photos upload failed — other edits are saved');
        } finally {
          setUploading(false);
        }
      }
      toast.success('Lead updated');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="lead-edit-dialog">
        <DialogHeader>
          <DialogTitle>Edit Lead — {lead.customer_name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Customer Name</Label>
            <Input value={form.customer_name || ''} onChange={set('customer_name')} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Phone</Label>
              <Input value={form.customer_phone || ''} onChange={set('customer_phone')} />
            </div>
            <div>
              <Label className="text-xs">Source</Label>
              <Select value={form.source || ''} onValueChange={setSel('source')}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>{SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs">Location</Label>
            <Input value={form.location || ''} onChange={set('location')} placeholder="e.g. Hubballi, Vidyanagar" data-testid="lead-edit-location" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Visit Date</Label>
              <Input type="date" value={form.visit_date || ''} onChange={set('visit_date')} data-testid="lead-edit-visit-date" />
            </div>
            <div>
              <Label className="text-xs">Follow-up Date</Label>
              <Input type="date" value={form.follow_up_date || ''} onChange={set('follow_up_date')} data-testid="lead-edit-followup" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Architect Name</Label>
              <Input value={form.architect_name || ''} onChange={set('architect_name')} placeholder="optional" data-testid="lead-edit-architect" />
            </div>
            <div>
              <Label className="text-xs">Carpenter Name</Label>
              <Input value={form.carpenter_name || ''} onChange={set('carpenter_name')} placeholder="optional" data-testid="lead-edit-carpenter" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Interest</Label>
              <Select value={form.interest_area || ''} onValueChange={setSel('interest_area')}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>{INTERESTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Budget</Label>
              <Input value={form.budget_range || ''} onChange={set('budget_range')} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Timeline</Label>
              <Select value={form.timeline || ''} onValueChange={setSel('timeline')}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>{TIMELINES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Follow-up Date</Label>
              <Input type="date" value={form.follow_up_date || ''} onChange={set('follow_up_date')} />
            </div>
          </div>
          <div>
            <Label className="text-xs flex items-center gap-1.5">
              <Camera className="w-3.5 h-3.5 text-[#2F6B8A]" />
              Site Photos <span className="text-[#8F9485]">(up to {MAX_SITE_IMAGES})</span>
            </Label>
            <div className="mt-1 flex flex-wrap gap-2" data-testid="lead-edit-site-photos">
              {existingPhotos.map((name) => (
                <div
                  key={name}
                  className="relative w-16 h-16 rounded-md overflow-hidden border border-[#E2E6D9] bg-[#F9FAF7]"
                  data-testid={`lead-edit-existing-photo-${name}`}
                >
                  <a href={`${API}/uploads/${name}`} target="_blank" rel="noreferrer" title="Open">
                    <img src={`${API}/uploads/${name}`} alt="" className="w-full h-full object-cover" loading="lazy" />
                  </a>
                  <button
                    type="button"
                    onClick={() => removeExistingPhoto(name)}
                    className="absolute top-0.5 right-0.5 bg-black/60 hover:bg-black/80 text-white rounded-full w-4 h-4 flex items-center justify-center"
                    aria-label="Delete photo"
                    data-testid={`lead-edit-delete-photo-${name}`}
                  >
                    <X className="w-2.5 h-2.5" />
                  </button>
                </div>
              ))}
              {pendingPreviews.map((url, idx) => (
                <div
                  key={url}
                  className="relative w-16 h-16 rounded-md overflow-hidden border border-dashed border-[#B7BFA8] bg-[#F9FAF7]"
                  data-testid={`lead-edit-pending-photo-${idx}`}
                >
                  <img src={url} alt="" className="w-full h-full object-cover opacity-90" />
                  <button
                    type="button"
                    onClick={() => removePendingPhoto(idx)}
                    className="absolute top-0.5 right-0.5 bg-black/60 hover:bg-black/80 text-white rounded-full w-4 h-4 flex items-center justify-center"
                    aria-label="Remove"
                    data-testid={`lead-edit-remove-pending-${idx}`}
                  >
                    <X className="w-2.5 h-2.5" />
                  </button>
                </div>
              ))}
              {remainingSlots > 0 && (
                <label
                  className="w-16 h-16 rounded-md border border-dashed border-[#B7BFA8] bg-[#F9FAF7] flex flex-col items-center justify-center cursor-pointer hover:bg-[#F2F4EF]"
                  data-testid="lead-edit-photo-add"
                >
                  <Camera className="w-4 h-4 text-[#5C6150]" />
                  <span className="text-[9px] text-[#5C6150] mt-0.5">Add</span>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={handlePickPhotos}
                    data-testid="lead-edit-photo-input"
                  />
                </label>
              )}
            </div>
            <p className="text-[9px] text-[#8F9485] mt-1">
              {existingPhotos.length + pendingPhotos.length}/{MAX_SITE_IMAGES} · new photos upload when you click Save.
            </p>
          </div>
          <div>
            <Label className="text-xs">Notes</Label>
            <Textarea value={form.notes || ''} onChange={set('notes')} rows={3} />
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-3">
          <Button variant="outline" onClick={onClose} disabled={saving || uploading}>Cancel</Button>
          <Button onClick={handleSave} className="bg-[#2F6B8A] hover:bg-[#235775] text-white" disabled={saving || uploading} data-testid="lead-edit-save">
            {saving || uploading ? (uploading ? 'Uploading…' : 'Saving…') : 'Save'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
